import { useRef, useState } from "react";
import { SpaceBackground } from "./SpaceBackground";
import { StormLayer } from "./StormLayer";
import { MobileBoot } from "./IntroMobile";
import { IntroReadyPanel } from "./Intro";

export const IntroDesktop = ({ onEnter, stormLevel = 0 }) => {
  // phases: start | boot | ready
  const [phase, setPhase] = useState("start");
  const [bgOn, setBgOn] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const codingRef = useRef(null);
  const successRef = useRef(null);
  const started = useRef(false);

  // silent per-element unlock — MUST be fully synchronous inside the click gesture
  const unlockAudio = () => {
    [codingRef.current, successRef.current].forEach((a) => {
      if (!a) return;
      a.volume = 0;
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
      a.pause();
      a.currentTime = 0;
    });
  };

  const handleStart = () => {
    if (started.current) return;
    started.current = true;
    unlockAudio();
    setBgOn(true);
    setTimeout(() => {
      const coding = codingRef.current;
      if (coding) {
        coding.volume = 0.45;
        coding.currentTime = 0;
        coding.play().catch((err) => console.error("Coding Noise play() failed:", err));
      }
      setPhase("boot");
    }, 400);
  };

  const skipIntro = () => {
    if (skipping || phase === "ready") return;
    setSkipping(true);
    [codingRef.current, successRef.current].forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
    setTimeout(() => {
      setPhase("ready");
      setSkipping(false);
    }, 350);
  };

  const handleUnleash = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onEnter(), 1000);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#050409] overflow-hidden"
      style={{ height: "100dvh" }}
      data-testid="intro-desktop-screen"
    >
      <audio ref={codingRef} src="/assets/audio/coding-noise-v2.mp3" preload="auto" loop data-testid="intro-coding-audio" />
      <audio ref={successRef} src="/assets/audio/success-noise-v2.mp3" preload="auto" data-testid="intro-success-audio" />

      {bgOn && (
        <div className="intro-fadein absolute inset-0" data-testid="intro-desktop-bg">
          <SpaceBackground />
          <StormLayer level={Math.max(stormLevel, 2)} />
        </div>
      )}

      {phase === "start" && (
        <button
          onClick={handleStart}
          data-testid="intro-desktop-click-to-start"
          className="absolute inset-0 z-20 w-full cursor-pointer bg-[#050409]"
          style={{ opacity: bgOn ? 0 : 1, transition: "opacity 0.4s ease", pointerEvents: bgOn ? "none" : "auto" }}
        >
          <div className="relative h-full flex flex-col items-center justify-center">
            <span
              className="font-bebas text-white tracking-[0.45em] pl-[0.45em] text-2xl"
              style={{ textShadow: "0 0 22px rgba(153,69,255,0.85), 0 0 44px rgba(153,69,255,0.4)" }}
            >
              CLICK TO ENTER THE STORM
            </span>
          </div>
        </button>
      )}

      {phase === "boot" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center intro-fadein">
          <MobileBoot onFinished={() => setPhase("ready")} codingRef={codingRef} successRef={successRef} />
        </div>
      )}

      {phase === "ready" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <IntroReadyPanel onEnter={handleUnleash} />
        </div>
      )}

      {phase === "boot" && (
        <button onClick={skipIntro} data-testid="intro-skip-button" className="skip-intro-btn font-bebas tracking-[0.25em] text-sm">
          SKIP INTRO
        </button>
      )}

      <div
        className="absolute inset-0 z-[60] bg-black pointer-events-none"
        style={{ opacity: skipping ? 1 : 0, transition: "opacity 0.35s ease" }}
      />
      <div
        className="absolute inset-0 z-[70] bg-black pointer-events-none"
        style={{ opacity: exiting ? 1 : 0, transition: "opacity 1s ease" }}
        data-testid="intro-exit-fade"
      />
    </div>
  );
};
